import { useState } from "react";
import { useTranslation } from "@privacy/translations";
import type { FC, ReactNode } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { CircularProgress } from "@privacy/ui";
import { useForm, useWatch } from "react-hook-form";
import { PageTitle, PageWrapper } from "../../shared/Page";
import { DownloadIcon, FileSpreadsheetIcon, PlusIcon } from "../../shared/icons";
import {
  useCreateDataImport,
  useGetDataImports,
  useGetDataImportTemplate,
  useGetDataImportValidatedFile,
} from "../../api/dataImport";
import * as FormDialog from "../../shared/FormDialog";
import { ErrorMessage } from "../../dynamicForm/inlineForm/ErrorMessage";
import { downloadBlob } from "../../shared/downloadBlob";
import { IsDataImportResponse } from "../../api/dataImport.types";
import { trackEvent } from "../../shared/Snowplow";
import ErrorImage from "./img/data-import-error.svg";
import ValidatingImage from "./img/data-import-validating.svg";

export const DataImportPage = () => {
  const [openCreateImportDialog, setOpenCreateImportDialog] = useState(false);
  const { t } = useTranslation();
  const { data, isFetching } = useGetDataImports({ refetchInterval: 1000 * 30 }); //Refetch data every 30 seconds. This is done incase user stays on page waiting for updates.
  const downloadTemplateMutation = useGetDataImportTemplate();
  const downloadValidationFileMutation = useGetDataImportValidatedFile();

  return (
    <PageWrapper>
      <Stack>
        <Stack sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <PageTitle title={t("DataImport.Title")} subTitle={t("DataImport.SubTitle")} />
            <CircularProgress
              sx={{
                padding: 0,
                visibility: isFetching ? "visible" : "hidden",
                transition: isFetching ? "none" : "visibility 0.1s 1s",
              }}
              size={24}
            />
          </Box>
          {openCreateImportDialog && <CreateDataImportDialog onClose={() => setOpenCreateImportDialog(false)} />}
        </Stack>
        <Box
          sx={[
            {
              maxWidth: "900px",
              paddingX: 12,
              paddingY: 4,
              borderRadius: 2,
              alignSelf: "center",
            },
            theme => ({ backgroundColor: theme.palette.background.paper }),
          ]}
        >
          <Stack sx={{ alignItems: "center", gap: 6, paddingBottom: 6 }}>
            {IsDataImportResponse(data) ? (
              <Grid spacing={8} container sx={{ alignItems: "center", py: 4 }}>
                <Grid size={6}>
                  <Stack spacing={5} sx={{ alignItems: "flex-start" }}>
                    <Typography variant="h2">
                      {t(`DataImport.Status.Message.${data.dataImport.status}`, {
                        dataImportName: data.dataImport.name,
                      })}
                    </Typography>
                    {data.dataImport.status === "validationSucceeded" && (
                      <Typography variant="h2">{t("DataImport.Status.Message2.validationSucceeded")}</Typography>
                    )}
                    <Stack sx={{ gap: 4, width: "300px" }}>
                      <DownloadFileButton label={t("DataImport.Template")} mutation={downloadTemplateMutation} />
                    </Stack>
                    {(data.dataImport.status === "validationFailed" || data.dataImport.status === "failed") && (
                      <Stack sx={{ gap: 4, width: "300px" }}>
                        <DownloadFileButton
                          label={t("DataImport.DownloadFile")}
                          mutation={downloadValidationFileMutation}
                        />
                        <Button
                          sx={{ whiteSpace: "nowrap", backgroundColor: "primary" }}
                          onClick={() => setOpenCreateImportDialog(true)}
                          startIcon={<PlusIcon size={16} />}
                        >
                          {t("DataImport.UploadButton")}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid size={6} sx={{ overflow: "hidden", justifyContent: "center", display: "flex" }}>
                  {(data.dataImport.status === "created" ||
                    data.dataImport.status === "validating" ||
                    data.dataImport.status === "validationSucceeded") && (
                    <img src={ValidatingImage} width="340" height="340" aria-hidden alt="" />
                  )}
                  {(data.dataImport.status === "validationFailed" || data.dataImport.status === "failed") && (
                    <img src={ErrorImage} width="340" height="340" aria-hidden alt="" />
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid spacing={8} container sx={{ alignItems: "center", py: 4 }}>
                <Grid size={6}>
                  <Stack spacing={5} sx={{ alignItems: "flex-start" }}>
                    <Typography variant="h2">{t(`DataImport.NoImport.Message`)}</Typography>
                  </Stack>
                </Grid>
                <Grid size={6} sx={{ overflow: "hidden" }}>
                  <Stack sx={{ gap: 4, width: "300px" }}>
                    <DownloadFileButton label={t("DataImport.Template")} mutation={downloadTemplateMutation} />
                    <Button
                      sx={{ whiteSpace: "nowrap", backgroundColor: "primary" }}
                      onClick={() => setOpenCreateImportDialog(true)}
                      startIcon={<PlusIcon size={16} />}
                    >
                      {t("DataImport.CreateButton")}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Stack>
        </Box>
      </Stack>
    </PageWrapper>
  );
};

const CreateDataImportDialog: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    control,
  } = useForm();
  const { mutate: createDataImport, isError } = useCreateDataImport();

  const file = useWatch({ control, name: "file" });

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("DataImport.CreateButton")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(() => {
          return createDataImport(
            { file: file[0] },
            {
              onSuccess: onClose,
              onSettled: () => {
                resetField("file");
              },
            }
          );
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={false} />
          <FormDialog.Fields>
            <FormDialog.Field
              errorMessage={
                errors.file ? t("Input.Validation.Required") : isError ? t("FormDialog.GenericError") : false
              }
            >
              <FormDialog.Label htmlFor="file">{t("DataImport.UploadButton")}</FormDialog.Label>
              <UploadFileButton file={file}>
                <input
                  {...register("file", {
                    required: true,
                  })}
                  id="file"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  style={{ display: "none" }}
                />
              </UploadFileButton>
            </FormDialog.Field>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={false}>{t("DataImport.Upload")}</FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

const Filename: FC<{ files: File[] }> = ({ files }) => {
  const sizeInMb = (files?.[0].size / 1e6).toFixed(2); // Convert bytes to megabytes and format to 2 decimal places
  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Typography variant="button">{files?.[0].name}</Typography>
      <Typography
        sx={{ fontStyle: "italic", color: theme => theme.palette.blue["200"], fontSize: "small", marginLeft: "auto" }}
      >
        {sizeInMb} MB
      </Typography>
    </Box>
  );
};

const UploadFileButton: FC<{ children?: ReactNode; onClick?: () => void; file?: File[] }> = ({
  children,
  onClick,
  file,
}) => {
  const { t } = useTranslation();

  return (
    <Button
      component={"label"}
      variant="outlined"
      fullWidth
      startIcon={<FileSpreadsheetIcon />}
      onClick={onClick}
      sx={{
        "color": theme => theme.palette.blue["500"],
        "&.MuiButtonBase-root": {
          "border": theme => `1px solid ${theme.palette.blue["200"]}`,
          "&:focus": { boxShadow: theme => `inset 0 0 0 1px ${theme.palette.blue["500"]}` },
        },
      }}
    >
      {file?.length ? <Filename files={file} /> : <Typography>{t("DataImport.UploadButton")}</Typography>}
      {children}
    </Button>
  );
};

const DownloadFileButton: FC<{
  label: string;
  mutation: ReturnType<typeof useGetDataImportTemplate | typeof useGetDataImportValidatedFile>;
}> = ({ label, mutation }) => {
  const { t } = useTranslation();
  const { mutate: download, isError, isPending } = mutation;

  return isError ? (
    <ErrorMessage errorMessage={t("FormDialog.GenericError")} />
  ) : (
    <Button
      variant="outlined"
      startIcon={<DownloadIcon size={20} />}
      loading={isPending}
      onClick={() => {
        trackEvent.clickDataImportDownloadFile(label);
        download(undefined, {
          onSuccess: ({ response, filename }) => {
            downloadBlob(response, filename);
          },
        });
      }}
    >
      {label}
    </Button>
  );
};
